/**
 * Trusscore Estimator - Trim Calculations
 *
 * Calculates J-Trim, Base Trim, Inside Cove, and Outside Corner requirements.
 *
 * Ported from: calculateCommonWallTrims_Helper() in estimator.js
 */

import { MaterialItem, ProductColor, CoreDimensions, WallConfig, GarageConfig } from '../types';
import {
  getJTrimHalfProduct,
  getJTrimThreeQuarterProduct,
  getJTrimSnapInProduct,
  getHTrimProduct,
  findProductById,
} from '../data/productTable';
import { TRIM_STOCK_LENGTH } from './constants';

export interface TrimResult {
  // Snap-In Trim J — produced by all current calc paths. Replaces legacy 1/2" + 3/4" J-Trims.
  jTrimsSnapIn: MaterialItem[];
  // Retained as empty arrays for back-compat with consumers that still read them.
  // Never populated by new code.
  jTrimsHalfInch: MaterialItem[];
  jTrimsThreeQuarter: MaterialItem[];
  baseTrims: MaterialItem[];
  insideCoveTrims: MaterialItem[];
  outsideCornerTrims: MaterialItem[];
  hTrims: MaterialItem[];
}

/**
 * @deprecated Snap-In Trim J replaces both 1/2" and 3/4" J-Trim as of the Snap-In rollout.
 * No production code calls this; retained only for any legacy tooling that may import it
 * via the default export.
 */
export function calculateJTrimHalfInch(
  wallNetLength: number,
  wallHeight: number,
  color: ProductColor,
  includeTopEdge: boolean = true,
  includeBottomEdge: boolean = true,
  includeSideEdges: boolean = true
): MaterialItem[] {
  const jTrims: MaterialItem[] = [];

  let totalLength = 0;

  if (includeTopEdge) {
    totalLength += wallNetLength;
  }

  if (includeBottomEdge) {
    totalLength += wallNetLength;
  }

  if (includeSideEdges) {
    totalLength += wallHeight * 2; // Both sides
  }

  if (totalLength <= 0) {
    return jTrims;
  }

  const quantity = Math.ceil(totalLength / TRIM_STOCK_LENGTH);
  const product = getJTrimHalfProduct(color);

  if (product && quantity > 0) {
    jTrims.push({
      productNumber: product.productNumber,
      productID: product.productID,
      name: product.name,
      color: product.color as ProductColor,
      quantity,
      lengthFeet: product.lengthFeet,
      sqft: 0,
      type: 'J_Trim_Half_Inch',
    });
  }

  return jTrims;
}

/**
 * @deprecated Snap-In Trim J replaces both 1/2" and 3/4" J-Trim as of the Snap-In rollout.
 * No production code calls this; retained only for any legacy tooling that may import it
 * via the default export.
 */
export function calculateJTrimThreeQuarter(
  slatWallLength: number,
  slatWallHeight: number,
  color: ProductColor
): MaterialItem[] {
  const jTrims: MaterialItem[] = [];

  if (slatWallLength <= 0 || slatWallHeight <= 0) {
    return jTrims;
  }

  // Perimeter of SlatWall section
  const totalLength = (slatWallLength * 2) + (slatWallHeight * 2);
  const quantity = Math.ceil(totalLength / TRIM_STOCK_LENGTH);

  const product = getJTrimThreeQuarterProduct(color);

  if (product && quantity > 0) {
    jTrims.push({
      productNumber: product.productNumber,
      productID: product.productID,
      name: product.name,
      color: product.color as ProductColor,
      quantity,
      lengthFeet: product.lengthFeet,
      sqft: 0,
      type: 'J_Trim_ThreeQuarter_SW',
    });
  }

  return jTrims;
}

/**
 * Calculate Base Trim for wall bottoms.
 *
 * @param wallNetLength - Net wall length
 * @param color - Trim color
 * @returns Base trim material items
 */
export function calculateBaseTrim(
  wallNetLength: number,
  color: ProductColor
): MaterialItem[] {
  const baseTrims: MaterialItem[] = [];

  if (wallNetLength <= 0) {
    return baseTrims;
  }

  const quantity = Math.ceil(wallNetLength / TRIM_STOCK_LENGTH);
  const product = findProductById(`Base10${color}`);

  if (product && quantity > 0) {
    baseTrims.push({
      productNumber: product.productNumber,
      productID: product.productID,
      name: product.name,
      color: product.color as ProductColor,
      quantity,
      lengthFeet: product.lengthFeet,
      sqft: 0,
      type: 'Base_Trim',
    });
  }

  return baseTrims;
}

/**
 * Calculate Inside Cove Trim for wall-ceiling junctions.
 *
 * @param perimeterLength - Total perimeter length
 * @param color - Trim color
 * @returns Inside cove trim material items
 */
export function calculateInsideCoveTrim(
  perimeterLength: number,
  color: ProductColor
): MaterialItem[] {
  const coveTrims: MaterialItem[] = [];

  if (perimeterLength <= 0) {
    return coveTrims;
  }

  const quantity = Math.ceil(perimeterLength / TRIM_STOCK_LENGTH);
  const product = findProductById(`Cove10${color}`);

  if (product && quantity > 0) {
    coveTrims.push({
      productNumber: product.productNumber,
      productID: product.productID,
      name: product.name,
      color: product.color as ProductColor,
      quantity,
      lengthFeet: product.lengthFeet,
      sqft: 0,
      type: 'Inside_Cove_Trim',
    });
  }

  return coveTrims;
}

/**
 * Calculate Outside Corner Trim.
 *
 * @param cornerHeight - Height of the corner
 * @param numCorners - Number of outside corners
 * @param color - Trim color
 * @param cornerType - Type of corner: 'large', 'small', or 'rounded'
 * @returns Outside corner trim material items
 */
export function calculateOutsideCornerTrim(
  cornerHeight: number,
  numCorners: number,
  color: ProductColor,
  cornerType: 'large' | 'small' | 'rounded' = 'large'
): MaterialItem[] {
  const cornerTrims: MaterialItem[] = [];

  if (cornerHeight <= 0 || numCorners <= 0) {
    return cornerTrims;
  }

  const totalLength = cornerHeight * numCorners;
  const quantity = Math.ceil(totalLength / TRIM_STOCK_LENGTH);

  const acronymMap = {
    large: 'LOC',
    small: 'SOC',
    rounded: 'OCR',
  };

  const productId = `${acronymMap[cornerType]}10${color}`;
  const product = findProductById(productId);

  if (product && quantity > 0) {
    cornerTrims.push({
      productNumber: product.productNumber,
      productID: product.productID,
      name: product.name,
      color: product.color as ProductColor,
      quantity,
      lengthFeet: product.lengthFeet,
      sqft: 0,
      type: `Outside_Corner_${cornerType.charAt(0).toUpperCase() + cornerType.slice(1)}`,
    });
  }

  return cornerTrims;
}

/**
 * Calculate J-Trims for garage door perimeter with zone-aware logic.
 *
 * Garage door trim rules:
 * - SIDES: Split by material zones
 *   - Portion in W&CB zone: 1/2" J-Trim (wall color)
 *   - Portion in SlatWall zone: 3/4" J-Trim (SlatWall color)
 * - TOP: Based on material above door
 *   - If SlatWall is above door opening: 3/4" J-Trim (SlatWall color)
 *   - If W&CB is above door opening: 1/2" J-Trim (wall color)
 *
 * Note: Garage doors do NOT use H-Trim at all - only J-Trim.
 *
 * @param garageConfig - Garage door configuration
 * @param wallColor - Wall color, used for Snap-In trim in the W&CB zone
 * @param slatWallColor - SlatWall color, used for Snap-In trim in the SlatWall zone
 * @param slatWallEnabled - Whether SlatWall is enabled
 * @param slatWallStartHeight - Height where SlatWall starts (from floor)
 * @param slatWallEndHeight - Height where SlatWall ends (from floor)
 * @returns Object containing Snap-In Trim J items, split by zone color
 */
export function calculateGarageDoorTrims(
  garageConfig: GarageConfig,
  wallColor: ProductColor,
  slatWallColor: ProductColor,
  slatWallEnabled: boolean,
  slatWallStartHeight: number,
  slatWallEndHeight: number
): { jTrimsSnapIn: MaterialItem[] } {
  const result: { jTrimsSnapIn: MaterialItem[] } = {
    jTrimsSnapIn: [],
  };

  if (!garageConfig.enabled || garageConfig.numDoors <= 0) {
    return result;
  }

  const doorHeight = garageConfig.height;
  const doorWidth = garageConfig.width;
  const numDoors = garageConfig.numDoors;

  // Snap-In Trim J unifies trim size across W&CB and SlatWall zones, but trim COLOR
  // still tracks the wall color in each zone. Accumulate linear feet per zone color.
  let wallColorLinearFeet = 0;
  let slatWallColorLinearFeet = 0;

  if (slatWallEnabled && slatWallStartHeight < doorHeight && slatWallEndHeight > 0) {
    // SlatWall overlaps with garage door - need to split by zones

    // SIDES: Calculate portion in each zone
    // Door runs from floor (0) to doorHeight
    // SlatWall runs from slatWallStartHeight to slatWallEndHeight

    // W&CB below SlatWall (from floor to SlatWall start)
    const wcbBelowHeight = Math.min(doorHeight, Math.max(0, slatWallStartHeight));

    // SlatWall zone within door height
    const swOverlapStart = Math.max(0, slatWallStartHeight);
    const swOverlapEnd = Math.min(doorHeight, slatWallEndHeight);
    const swOverlapHeight = Math.max(0, swOverlapEnd - swOverlapStart);

    // W&CB above SlatWall (from SlatWall end to door top) - typically 0 for garage doors
    const wcbAboveHeight = Math.max(0, doorHeight - slatWallEndHeight);

    // Side trim lengths (× 2 sides × numDoors)
    wallColorLinearFeet += (wcbBelowHeight + wcbAboveHeight) * 2 * numDoors;
    slatWallColorLinearFeet += swOverlapHeight * 2 * numDoors;

    // TOP: trim color follows the zone covering the top of the door
    if (slatWallEndHeight > doorHeight) {
      // SlatWall is above the door - top trim takes SlatWall color
      slatWallColorLinearFeet += doorWidth * numDoors;
    } else {
      // W&CB is above the door - top trim takes wall color
      wallColorLinearFeet += doorWidth * numDoors;
    }
  } else {
    // No SlatWall or SlatWall doesn't overlap with door - entire perimeter takes wall color
    const sidesLength = doorHeight * 2 * numDoors;
    const topLength = doorWidth * numDoors;
    wallColorLinearFeet = sidesLength + topLength;
  }

  // Push per-color Snap-In items. Same color in both buckets de-dupes in consolidation.
  if (wallColorLinearFeet > 0) {
    const quantity = Math.ceil(wallColorLinearFeet / TRIM_STOCK_LENGTH);
    const product = getJTrimSnapInProduct(wallColor);
    if (product && quantity > 0) {
      result.jTrimsSnapIn.push({
        productNumber: product.productNumber,
        productID: product.productID,
        name: product.name,
        color: product.color as ProductColor,
        quantity,
        lengthFeet: product.lengthFeet,
        sqft: 0,
        type: 'J_Trim_SnapIn_Garage',
      });
    }
  }

  if (slatWallColorLinearFeet > 0) {
    const quantity = Math.ceil(slatWallColorLinearFeet / TRIM_STOCK_LENGTH);
    const product = getJTrimSnapInProduct(slatWallColor);
    if (product && quantity > 0) {
      result.jTrimsSnapIn.push({
        productNumber: product.productNumber,
        productID: product.productID,
        name: product.name,
        color: product.color as ProductColor,
        quantity,
        lengthFeet: product.lengthFeet,
        sqft: 0,
        type: 'J_Trim_SnapIn_Garage',
      });
    }
  }

  return result;
}

/**
 * Calculate all common wall trims for a wall section.
 *
 * Port of calculateCommonWallTrims_Helper from estimator.js
 *
 * The legacy estimator calculates J-Trim for:
 * - Wall corners (both sides): height × 2 (1/2" for W&CB sections, 3/4" for slatwall sections)
 * - Wall bottom edge: length (1/2" for W&CB, 3/4" if slatwall starts at floor)
 * - Wall top edge: length (1/2" for W&CB, 3/4" if slatwall reaches ceiling without cap trim)
 *
 * NOTE: 3/4" J-trim for slatwall sections is calculated here, NOT in calculateSlatWallJTrims
 * to match the legacy behavior where all J-trims are calculated in CommonTrims.
 *
 * @param coreDims - Core dimensions for the wall
 * @param wallConfig - Wall configuration
 * @param includeCeiling - Whether ceiling is included (affects inside cove)
 * @returns Complete trim result for the wall
 */
export function calculateCommonWallTrims(
  coreDims: CoreDimensions,
  wallConfig: WallConfig,
  includeCeiling: boolean = false
): TrimResult {
  const result: TrimResult = {
    jTrimsSnapIn: [],
    jTrimsHalfInch: [],
    jTrimsThreeQuarter: [],
    baseTrims: [],
    insideCoveTrims: [],
    outsideCornerTrims: [],
    hTrims: [],
  };

  if (!wallConfig.enabled || coreDims.wallNetLength <= 0) {
    return result;
  }

  // Snap-In Trim J unifies trim SIZE across W&CB and SlatWall zones. Trim COLOR still
  // follows the zone color, so we track linear feet per zone color and emit one Snap-In
  // item per color used.
  let wallColorLinearFeet = 0;
  let slatWallColorLinearFeet = 0;

  const slatWallEnabled = wallConfig.slatWall.enabled && coreDims.slatWallCalculatedHeight > 0;

  // 1. CORNER/VERTICAL EDGES (runs full height on both sides of wall)
  if (slatWallEnabled) {
    if (coreDims.wallheightBelowSW > 0) {
      wallColorLinearFeet += coreDims.wallheightBelowSW * 2;
    }
    if (coreDims.slatWallCalculatedHeight > 0) {
      slatWallColorLinearFeet += coreDims.slatWallCalculatedHeight * 2;
    }
    if (coreDims.wallheightAboveSW > 0) {
      wallColorLinearFeet += coreDims.wallheightAboveSW * 2;
    }
  } else {
    if (coreDims.commonRoomHeight > 0) {
      wallColorLinearFeet += coreDims.commonRoomHeight * 2;
    }
  }

  // 2. BOTTOM EDGE — color follows the zone that meets the floor
  if (slatWallEnabled && wallConfig.slatWall.startHeight === 0) {
    slatWallColorLinearFeet += coreDims.wallNetLength;
  } else {
    wallColorLinearFeet += coreDims.wallNetLength;
  }

  // 3. TOP EDGE
  // The TOP edge always uses FULL wall length - garage door opening only affects the bottom
  // For walls without garage: wallConfig.length === coreDims.wallNetLength (no difference)
  // For walls with garage: top edge runs full width, bottom edge is reduced by garage opening
  const topEdgeLength = wallConfig.length;

  // Check if slatwall reaches the ceiling
  const slatWallTopY = slatWallEnabled
    ? (wallConfig.slatWall.startHeight + wallConfig.slatWall.numPanelRows * 0.5) // 0.5ft per row
    : 0;
  const slatWallReachesCeiling = slatWallEnabled && (slatWallTopY + 1/12) >= coreDims.commonRoomHeight; // 1" tolerance

  if (slatWallEnabled && slatWallReachesCeiling && coreDims.wallheightAboveSW <= 0) {
    // SlatWall reaches ceiling with no W&CB above - top trim takes SlatWall color
    slatWallColorLinearFeet += topEdgeLength;
  } else {
    wallColorLinearFeet += topEdgeLength;
  }

  // Convert to Snap-In product quantities (per zone color)
  if (wallColorLinearFeet > 0) {
    const quantity = Math.ceil(wallColorLinearFeet / TRIM_STOCK_LENGTH);
    const product = getJTrimSnapInProduct(wallConfig.color);
    if (product && quantity > 0) {
      result.jTrimsSnapIn.push({
        productNumber: product.productNumber,
        productID: product.productID,
        name: product.name,
        color: product.color as ProductColor,
        quantity,
        lengthFeet: product.lengthFeet,
        sqft: 0,
        type: 'J_Trim_SnapIn',
      });
    }
  }

  if (slatWallColorLinearFeet > 0) {
    const quantity = Math.ceil(slatWallColorLinearFeet / TRIM_STOCK_LENGTH);
    const product = getJTrimSnapInProduct(wallConfig.slatWall.color);
    if (product && quantity > 0) {
      result.jTrimsSnapIn.push({
        productNumber: product.productNumber,
        productID: product.productID,
        name: product.name,
        color: product.color as ProductColor,
        quantity,
        lengthFeet: product.lengthFeet,
        sqft: 0,
        type: 'J_Trim_SnapIn',
      });
    }
  }

  // Note: Inside Cove Trim is not included in estimator scope

  // Garage door trims - use zone-aware calculation
  if (wallConfig.garage.enabled) {
    // Calculate SlatWall zone boundaries for the garage door trim calculation
    const slatWallStartHeight = slatWallEnabled ? wallConfig.slatWall.startHeight : 0;
    const slatWallEndHeight = slatWallEnabled
      ? wallConfig.slatWall.startHeight + (wallConfig.slatWall.numPanelRows * 0.5)
      : 0;

    const garageDoorTrims = calculateGarageDoorTrims(
      wallConfig.garage,
      wallConfig.color,
      wallConfig.slatWall.color,
      slatWallEnabled,
      slatWallStartHeight,
      slatWallEndHeight
    );

    result.jTrimsSnapIn.push(...garageDoorTrims.jTrimsSnapIn);

    // Note: Garage doors do NOT get H-Trim - only J-Trim
    // H-Trim is only for SlatWall/W&CB horizontal interfaces
  }

  return result;
}

/**
 * Calculate ceiling perimeter J-Trim.
 *
 * @param ceilingLength - Ceiling length in feet
 * @param ceilingWidth - Ceiling width in feet
 * @param color - Trim color
 * @returns J-Trim 1/2" for ceiling perimeter
 */
export function calculateCeilingPerimeterJTrim(
  ceilingLength: number,
  ceilingWidth: number,
  color: ProductColor
): MaterialItem[] {
  const jTrims: MaterialItem[] = [];

  if (ceilingLength <= 0 || ceilingWidth <= 0) {
    return jTrims;
  }

  const perimeter = (ceilingLength * 2) + (ceilingWidth * 2);
  const quantity = Math.ceil(perimeter / TRIM_STOCK_LENGTH);

  const product = getJTrimSnapInProduct(color);

  if (product && quantity > 0) {
    jTrims.push({
      productNumber: product.productNumber,
      productID: product.productID,
      name: product.name,
      color: product.color as ProductColor,
      quantity,
      lengthFeet: product.lengthFeet,
      sqft: 0,
      type: 'J_Trim_SnapIn_Ceiling_Perimeter',
    });
  }

  return jTrims;
}

/**
 * @deprecated No longer used - per-wall J-trim calculation is used for all modes.
 * Calculate J-Trims for FullRoom mode using legacy room-based formula.
 *
 * This function replicates the legacy estimator's J-trim calculation
 * which treats the room as a whole rather than calculating per-wall.
 *
 * Legacy formulas:
 * - 1/2" J-Trim: (perimeter × multiplier) + (W&CB height × 8 corners)
 * - 3/4" J-Trim: (perimeter if SW at top) + (SW height × 8 corners)
 *
 * @param roomPerimeter - Room perimeter (2×length + 2×width)
 * @param roomPerimeterNet - Room perimeter minus garage door widths
 * @param wallheightBelowSW - W&CB height below SlatWall
 * @param wallheightAboveSW - W&CB height above SlatWall
 * @param slatWallHeight - Total SlatWall height (0 if not enabled)
 * @param slatWallEnabled - Whether SlatWall is enabled
 * @param garageDoorHeight - Height of garage doors (0 if none)
 * @param numGarageDoors - Number of garage doors
 * @param wallColor - Color for 1/2" J-trim
 * @param slatWallColor - Color for 3/4" J-trim
 * @returns Object with both J-trim types
 */
export function calculateFullRoomJTrims(
  roomPerimeter: number,
  roomPerimeterNet: number,
  wallheightBelowSW: number,
  wallheightAboveSW: number,
  slatWallHeight: number,
  slatWallEnabled: boolean,
  garageDoorHeight: number,
  numGarageDoors: number,
  wallColor: ProductColor,
  slatWallColor: ProductColor
): { jTrimsHalfInch: MaterialItem[]; jTrimsThreeQuarter: MaterialItem[] } {
  const result: { jTrimsHalfInch: MaterialItem[]; jTrimsThreeQuarter: MaterialItem[] } = {
    jTrimsHalfInch: [],
    jTrimsThreeQuarter: [],
  };

  // Legacy calculation for 1/2" J-Trim
  // Note: "8" represents 8 vertical corner edges (4 corners × 2 edges each)
  const slatWallAtTop = wallheightAboveSW === 0 && slatWallEnabled && slatWallHeight > 0;

  let jTrimRequiredLength: number;
  if (slatWallAtTop) {
    // SlatWall reaches ceiling - only 1× perimeter for bottom edge
    if (garageDoorHeight > 0) {
      jTrimRequiredLength = roomPerimeterNet +
        ((wallheightBelowSW + wallheightAboveSW) * 8) +
        ((wallheightBelowSW + wallheightAboveSW) * 2 * numGarageDoors);
    } else {
      jTrimRequiredLength = roomPerimeterNet + ((wallheightBelowSW + wallheightAboveSW) * 8);
    }
  } else {
    // W&CB at top and bottom - 2× perimeter
    if (garageDoorHeight > 0) {
      jTrimRequiredLength = (roomPerimeterNet * 2) +
        ((wallheightBelowSW + wallheightAboveSW) * 8) +
        ((wallheightBelowSW + wallheightAboveSW) * 2 * numGarageDoors);
    } else {
      jTrimRequiredLength = (roomPerimeterNet * 2) + ((wallheightBelowSW + wallheightAboveSW) * 8);
    }
  }

  if (jTrimRequiredLength > 0) {
    const quantity = Math.ceil(jTrimRequiredLength / TRIM_STOCK_LENGTH);
    const product = getJTrimHalfProduct(wallColor);
    if (product && quantity > 0) {
      result.jTrimsHalfInch.push({
        productNumber: product.productNumber,
        productID: product.productID,
        name: product.name,
        color: product.color as ProductColor,
        quantity,
        lengthFeet: product.lengthFeet,
        sqft: 0,
        type: 'J_Trim_Half_FullRoom',
      });
    }
  }

  // Legacy calculation for 3/4" J-Trim (SlatWall)
  if (slatWallEnabled && slatWallHeight > 0) {
    let swJTrimRequiredLength: number;
    if (slatWallAtTop) {
      // SlatWall reaches ceiling - include top perimeter
      if (garageDoorHeight > 0) {
        swJTrimRequiredLength = roomPerimeterNet + (slatWallHeight * (8 + (2 * numGarageDoors)));
      } else {
        swJTrimRequiredLength = roomPerimeterNet + (slatWallHeight * 8);
      }
    } else {
      // SlatWall doesn't reach ceiling - only vertical corners
      if (garageDoorHeight > 0) {
        swJTrimRequiredLength = slatWallHeight * (8 + (2 * numGarageDoors));
      } else {
        swJTrimRequiredLength = slatWallHeight * 8;
      }
    }

    if (swJTrimRequiredLength > 0) {
      const quantity = Math.ceil(swJTrimRequiredLength / TRIM_STOCK_LENGTH);
      const product = getJTrimThreeQuarterProduct(slatWallColor);
      if (product && quantity > 0) {
        result.jTrimsThreeQuarter.push({
          productNumber: product.productNumber,
          productID: product.productID,
          name: product.name,
          color: product.color as ProductColor,
          quantity,
          lengthFeet: product.lengthFeet,
          sqft: 0,
          type: 'J_Trim_ThreeQuarter_FullRoom',
        });
      }
    }
  }

  return result;
}

export default {
  calculateJTrimHalfInch,
  calculateJTrimThreeQuarter,
  calculateBaseTrim,
  calculateInsideCoveTrim,
  calculateOutsideCornerTrim,
  calculateGarageDoorTrims,
  calculateCommonWallTrims,
  calculateCeilingPerimeterJTrim,
};
